import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Loader2, PenLine, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, euro } from "@/lib/api";
import { SITE_URL } from "@/lib/seo";

const FEED_URL = `${SITE_URL}/api/feeds/google-shopping.xml`;
const card = "rounded-sm border border-[var(--line)] p-6";

function Stat({ label, value, testid }) {
  return <div className="rounded-sm border border-[var(--line)] p-4"><p className="text-xs uppercase tracking-[0.18em] text-[var(--muted)]">{label}</p><p data-testid={testid} className="mt-2 font-display text-3xl">{value ?? 0}</p></div>;
}

function MerchantFeed({ count }) {
  const copy = () => navigator.clipboard?.writeText(FEED_URL).then(() => toast.success("Adresse copiée"));
  return (
    <section data-testid="automation-feed" className={card}>
      <p className="eyebrow mb-3 flex items-center gap-2"><ShoppingBag className="h-4 w-4" />Google Shopping gratuit</p>
      <p className="text-sm text-[var(--parchment)]/90">{count} produits dans le flux, mis à jour automatiquement (prix, stock, photos).</p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <code data-testid="automation-feed-url" className="break-all rounded-sm bg-[var(--ink-2)] px-3 py-2 text-xs">{FEED_URL}</code>
        <button data-testid="automation-feed-copy" onClick={copy} className="flex items-center gap-1.5 rounded-full border border-[var(--line)] px-4 py-2 text-xs hover:border-[var(--gold)]"><Copy className="h-3.5 w-3.5" />Copier</button>
      </div>
      <ol className="mt-4 list-decimal space-y-1 pl-5 text-sm text-[var(--parchment)]/85">
        <li>Merchant Center → Produits → Ajouter des produits → « Ajouter des produits à partir d'un fichier ».</li>
        <li>Choisissez « Ajouter un lien vers un fichier », collez l'adresse ci-dessus, fréquence : tous les jours.</li>
        <li>Vérifiez que l'option « Fiches gratuites » (fiches produit sans frais) est activée dans vos programmes.</li>
      </ol>
    </section>
  );
}

function SeoDraft({ state, pending, onRun, running }) {
  const label = { ok: "Dernier brouillon rédigé", error: "Échec de la dernière rédaction", running: "Rédaction en cours…" }[state?.status];
  return (
    <section data-testid="automation-seo" className={card}>
      <p className="eyebrow mb-3 flex items-center gap-2"><PenLine className="h-4 w-4" />Article SEO hebdomadaire</p>
      <p className="text-sm text-[var(--parchment)]/90">Chaque lundi à 8 h, un nouvel article est rédigé par l'IA et enregistré en brouillon (jamais publié automatiquement). Relisez-le dans l'onglet Conseils, puis cochez « Publié sur le site ».</p>
      <p data-testid="automation-seo-pending" className="mt-3 text-sm"><b>{pending}</b> brouillon{pending > 1 ? "s" : ""} IA à relire</p>
      {label ? <p data-testid="automation-seo-status" className="mt-2 text-xs text-[var(--muted)]">{label}{state?.title ? ` : « ${state.title} »` : ""}{state?.at ? ` · ${new Date(state.at).toLocaleString("fr-FR")}` : ""}</p> : null}
      <button data-testid="automation-seo-generate" onClick={onRun} disabled={running} className="mt-4 flex h-11 items-center gap-2 rounded-full bg-[var(--gold)] px-6 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink)] hover:bg-[var(--gold-soft)] disabled:opacity-50">
        {running ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Rédiger un brouillon maintenant
      </button>
    </section>
  );
}

function CartRecovery({ s }) {
  const cells = [
    ["Paniers relancés", s?.reminded ?? 0, "cart-reminded", `dont ${s?.code_sent ?? 0} avec le code -5 %`],
    ["Ont fini par commander", s?.recovered ?? 0, "cart-recovered", s?.rate == null ? "dans les 7 jours" : `${s.rate} % · dans les 7 jours`],
    ["Avec le code -5 %", s?.with_code ?? 0, "cart-with-code", "code de la 2e relance utilisé"],
    ["Sans code", s?.without_code ?? 0, "cart-without-code", "après la 1re relance ou sans le code"],
    ["Chiffre d'affaires récupéré", euro(s?.revenue ?? 0), "cart-revenue", "commandes payées TTC"],
  ];
  return (
    <section data-testid="automation-cart-recovery" className={card}>
      <p className="eyebrow mb-4">Relances de paniers abandonnés</p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        {cells.map(([label, value, testid, hint]) => (
          <div key={testid} className="rounded-sm border border-[var(--line)] p-3">
            <p className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">{label}</p>
            <p data-testid={testid} className="mt-1 font-display text-2xl">{value}</p>
            <p className="text-xs text-[var(--parchment)]/70">{hint}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

export function AutomationAdmin() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-automation"], queryFn: async () => (await api.get("/admin/automation")).data,
    refetchInterval: (q) => (q.state.data?.seo_draft?.status === "running" ? 5000 : false) });
  const running = data?.seo_draft?.status === "running";
  const run = async () => {
    try { await api.post("/admin/articles/generate-draft"); toast.success("Rédaction lancée : comptez 1 à 2 minutes."); qc.invalidateQueries({ queryKey: ["admin-automation"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-automation" className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Anniversaires enregistrés" value={data?.birthdays} testid="automation-birthdays" />
        <Stat label="Emails anniversaire" value={data?.birthday_emails} testid="automation-birthday-emails" />
        <Stat label="Relances consultation" value={data?.view_emails} testid="automation-view-emails" />
        <Stat label="Produits Google Shopping" value={data?.feed_products} testid="automation-feed-products" />
      </div>
      <CartRecovery s={data?.cart_recovery} />
      <div className="grid gap-6 lg:grid-cols-2">
        <MerchantFeed count={data?.feed_products ?? 0} />
        <SeoDraft state={data?.seo_draft} pending={data?.ai_drafts_pending ?? 0} onRun={run} running={running} />
      </div>
      <p className="text-xs text-[var(--muted)]">Emails automatiques : panier abandonné (rappel 3 à 24 h après, puis -5 % valable 48 h le lendemain si toujours pas de commande), réachat 30 jours après l'expédition, relance -5 % (7 jours) 24 h après la consultation d'un produit sans achat, -15 % (15 jours) le jour de l'anniversaire, rappel « choisissez vos soins » aux abonnées 3 jours avant chaque prélèvement (dès le 2e), alerte cliente + propriétaire si un prélèvement d'abonnement est refusé. Chaque email contient un lien de désinscription.</p>
    </div>
  );
}
